import { useRef, useEffect } from 'react';

interface UseUniqueValidatorOptions<T> {
  originalValue?: T; // value to skip in edit form
  checkUnique: (value: T) => Promise<boolean>; // API call to check uniqueness
  errorMessage?: string; // error message if not unique
  debounceTime?: number; // debounce delay in ms, default 500
}

export const useUniqueValidator = <T = string>({
  originalValue,
  checkUnique,
  errorMessage,
  debounceTime = 500,
}: UseUniqueValidatorOptions<T>) => {
  const lastValidatedValue = useRef<T | null>(null);
  const lastValidationResult = useRef<boolean | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPromise = useRef<{
    resolve: () => void;
    reject: (msg: string) => void;
  } | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (_: any, value: T) =>
    new Promise<void>((resolve, reject) => {
      // If value is empty, resolve immediately
      if (!value) {
        resolve();
        return;
      }

      // Skip validation if unchanged from original
      if (originalValue !== undefined && value === originalValue) {
        resolve();
        return;
      }

      // Use cached result if available
      if (value === lastValidatedValue.current) {
        if (lastValidationResult.current) {
          resolve();
        } else {
          reject(errorMessage || 'Value already exists');
        }
        return;
      }

      // Clear previous debounce
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (pendingPromise.current) {
        pendingPromise.current.reject('Validation cancelled');
      }

      pendingPromise.current = { resolve, reject };

      timeoutRef.current = setTimeout(async () => {
        try {
          const isUnique = await checkUnique(value);
          lastValidatedValue.current = value;
          lastValidationResult.current = isUnique;

          if (isUnique) {
            resolve();
          } else {
            reject(errorMessage || 'Value already exists');
          }
        } catch (error) {
          console.error('Uniqueness validation failed:', error);
          reject('Validation failed');
        } finally {
          pendingPromise.current = null;
        }
      }, debounceTime);
    });
};
